import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

export default function Settings() {
  const { apiFetch, user } = useAuth();
  const [settings, setSettings] = useState({});
  const [fiscalYears, setFiscalYears] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [smtp, setSmtp] = useState({ smtp_host: '', smtp_port: '587', smtp_user: '', smtp_password: '', smtp_from_name: '', smtp_from_email: '' });
  const [smtpMessage, setSmtpMessage] = useState('');
  const [smtpError, setSmtpError] = useState('');
  const [testingSmtp, setTestingSmtp] = useState(false);

  const load = () => {
    apiFetch('/api/settings')
      .then(s => setSettings(s))
      .catch(err => setError(prev => prev || err.message));

    apiFetch('/api/settings/fiscal-years')
      .then(fy => setFiscalYears(fy))
      .catch(err => setError(prev => prev || err.message));

    if (user?.role === 'admin') {
      apiFetch('/api/settings/smtp')
        .then(smtpData => setSmtp(prev => ({ ...prev, ...smtpData })))
        .catch(() => {});
    }
  };

  useEffect(() => { load(); }, []);

  const saveSettings = async (e) => {
    e.preventDefault();
    setMessage(''); setError('');
    try {
      await apiFetch('/api/settings', { method: 'PUT', body: settings });
      setMessage('Indstillinger gemt.');
    } catch (err) {
      setError(err.message);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setMessage(''); setError('');

    if (pwForm.newPassword !== pwForm.confirmPassword) {
      setError('De nye adgangskoder stemmer ikke overens.');
      return;
    }

    try {
      await apiFetch('/api/auth/change-password', {
        method: 'POST',
        body: { currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword }
      });
      setMessage('Adgangskode \u00e6ndret.');
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteAccountingData = async () => {
    setMessage(''); setError('');
    setDeleting(true);
    try {
      const res = await apiFetch('/api/settings/accounting-data', { method: 'DELETE' });
      setMessage(res.message);
      setShowDeleteConfirm(false);
      setDeleteConfirmText('');
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const [chainResult, setChainResult] = useState(null);

  const activateFy = async (id) => {
    try {
      await apiFetch(`/api/settings/fiscal-years/${id}/activate`, { method: 'PUT' });
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  const closeFy = async (id, force) => {
    try {
      await apiFetch(`/api/settings/fiscal-years/${id}/close`, { method: 'PUT', body: { force } });
      setMessage('Regnskabsår lukket.');
      load();
    } catch (err) {
      if (err.message.includes('huller') && !force) {
        if (window.confirm(`${err.message}\n\nVil du lukke alligevel?`)) {
          return closeFy(id, true);
        }
      } else {
        setError(err.message);
      }
    }
  };

  const reopenFy = async (id) => {
    if (!window.confirm('Er du sikker på at du vil genåbne dette regnskabsår?')) return;
    try {
      await apiFetch(`/api/settings/fiscal-years/${id}/reopen`, { method: 'PUT' });
      setMessage('Regnskabsår genåbnet.');
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const checkVoucherChain = async (fyId) => {
    try {
      const r = await apiFetch(`/api/settings/voucher-chain/${fyId}`);
      setChainResult(r);
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>Indstillinger</h1>
      </div>

      {message && <div className="alert alert-success">{message}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {user?.role === 'admin' && (
        <div className="card">
          <h3 style={{marginBottom: '1rem'}}>Foreningsoplysninger</h3>
          <form onSubmit={saveSettings}>
            <div className="form-row">
              <div className="form-group">
                <label>Foreningsnavn</label>
                <input className="form-control" value={settings.association_name || ''}
                  onChange={e => setSettings({...settings, association_name: e.target.value})} />
              </div>
              <div className="form-group">
                <label>CVR-nr.</label>
                <input className="form-control" value={settings.cvr || ''}
                  onChange={e => setSettings({...settings, cvr: e.target.value})} />
              </div>
            </div>
            <div className="form-group">
              <label>Adresse</label>
              <input className="form-control" value={settings.address || ''}
                onChange={e => setSettings({...settings, address: e.target.value})} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Postnr.</label>
                <input className="form-control" value={settings.postal_code || ''}
                  onChange={e => setSettings({...settings, postal_code: e.target.value})} />
              </div>
              <div className="form-group">
                <label>By</label>
                <input className="form-control" value={settings.city || ''}
                  onChange={e => setSettings({...settings, city: e.target.value})} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Telefon</label>
                <input className="form-control" value={settings.phone || ''}
                  onChange={e => setSettings({...settings, phone: e.target.value})} />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input className="form-control" value={settings.email || ''}
                  onChange={e => setSettings({...settings, email: e.target.value})} />
              </div>
            </div>
            <div className="form-group" style={{maxWidth: '200px'}}>
              <label>Standard betalingsfrist (dage)</label>
              <input type="number" className="form-control" value={settings.default_payment_days || 30}
                onChange={e => setSettings({...settings, default_payment_days: parseInt(e.target.value) || 30})}
                min="1" max="365" />
            </div>
            <button type="submit" className="btn btn-primary">Gem indstillinger</button>
          </form>
        </div>
      )}

      <div className="card" style={{marginTop: '1rem'}}>
        <h3 style={{marginBottom: '1rem'}}>Regnskabs&#229;r</h3>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Navn</th>
                <th>Start</th>
                <th>Slut</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {fiscalYears.map(fy => (
                <tr key={fy.id}>
                  <td>{fy.name}</td>
                  <td>{new Date(fy.start_date).toLocaleDateString('da-DK')}</td>
                  <td>{new Date(fy.end_date).toLocaleDateString('da-DK')}</td>
                  <td>
                    {fy.is_active ? <span className="badge badge-active">Aktiv</span> :
                     fy.is_closed ? <span className="badge badge-inactive">Lukket</span> :
                     <span className="badge">Inaktiv</span>}
                  </td>
                  <td className="text-right">
                    <div style={{display: 'flex', gap: '0.25rem', justifyContent: 'flex-end'}}>
                      {user?.role === 'admin' && (
                        <button className="btn btn-secondary btn-sm" onClick={() => checkVoucherChain(fy.id)} title="Tjek bilagsk&#230;de">Tjek</button>
                      )}
                      {!fy.is_active && !fy.is_closed && user?.role === 'admin' && (
                        <button className="btn btn-primary btn-sm" onClick={() => activateFy(fy.id)}>Aktiver</button>
                      )}
                      {!fy.is_closed && user?.role === 'admin' && (
                        <button className="btn btn-danger btn-sm" onClick={() => closeFy(fy.id, false)}>Luk</button>
                      )}
                      {fy.is_closed && user?.role === 'admin' && (
                        <button className="btn btn-secondary btn-sm" onClick={() => reopenFy(fy.id)}>Gen&#229;bn</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {chainResult && (
          <div style={{marginTop: '1rem', padding: '1rem', background: chainResult.is_valid ? '#f0fdf4' : '#fef2f2', border: `1px solid ${chainResult.is_valid ? '#86efac' : '#fecaca'}`, borderRadius: '8px'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <strong>{chainResult.is_valid ? 'Bilagsk\u00e6den er komplet' : 'Huller i bilagsk\u00e6den'} - {chainResult.fiscal_year}</strong>
              <button className="btn btn-secondary btn-sm" onClick={() => setChainResult(null)}>Luk</button>
            </div>
            <p style={{fontSize: '0.85rem', margin: '0.5rem 0 0'}}>
              Antal bilag: {chainResult.total_vouchers} | Nr. {chainResult.first_number || '-'} til {chainResult.last_number || '-'}
            </p>
            {chainResult.gaps.length > 0 && (
              <ul style={{fontSize: '0.85rem', margin: '0.5rem 0 0', paddingLeft: '1.25rem'}}>
                {chainResult.gaps.map((g, i) => (
                  <li key={i}>Mangler nr. {g.from}{g.from !== g.to ? ` til ${g.to}` : ''} ({g.missing} bilag)</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {user?.role === 'admin' && (
        <div className="card" style={{marginTop: '1rem'}}>
          <h3 style={{marginBottom: '0.5rem'}}>Email</h3>
          <div style={{background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', padding: '0.85rem', marginBottom: '1rem'}}>
            <strong style={{color: '#16a34a'}}>Email virker automatisk!</strong>
            <p style={{fontSize: '0.85rem', color: '#15803d', margin: '0.25rem 0 0 0'}}>
              Fakturaer og opkrævninger sendes via Foreningsbogføring's email-service. Du behøver ikke opsætte noget.
            </p>
          </div>
          <p style={{fontSize: '0.85rem', color: '#64748b', marginBottom: '1rem'}}>
            Vil du bruge din egen mailserver i stedet? Konfigurer SMTP herunder (valgfrit).
          </p>
          {smtpMessage && <div className="alert alert-success">{smtpMessage}</div>}
          {smtpError && <div className="alert alert-error">{smtpError}</div>}
          <form onSubmit={async (e) => {
            e.preventDefault();
            setSmtpMessage(''); setSmtpError('');
            try {
              await apiFetch('/api/settings/smtp', { method: 'PUT', body: smtp });
              setSmtpMessage('SMTP-indstillinger gemt.');
            } catch (err) { setSmtpError(err.message); }
          }}>
            <div className="form-row">
              <div className="form-group" style={{flex: 2}}>
                <label>SMTP Server</label>
                <input className="form-control" value={smtp.smtp_host || ''}
                  onChange={e => setSmtp({...smtp, smtp_host: e.target.value})}
                  placeholder="f.eks. smtp.gmail.com" />
              </div>
              <div className="form-group" style={{flex: 1}}>
                <label>Port</label>
                <input className="form-control" value={smtp.smtp_port || '587'}
                  onChange={e => setSmtp({...smtp, smtp_port: e.target.value})}
                  placeholder="587" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Brugernavn</label>
                <input className="form-control" value={smtp.smtp_user || ''}
                  onChange={e => setSmtp({...smtp, smtp_user: e.target.value})}
                  placeholder="din@email.dk" />
              </div>
              <div className="form-group">
                <label>Adgangskode</label>
                <input type="password" className="form-control" value={smtp.smtp_password || ''}
                  onChange={e => setSmtp({...smtp, smtp_password: e.target.value})}
                  placeholder="App-adgangskode" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Afsendernavn</label>
                <input className="form-control" value={smtp.smtp_from_name || ''}
                  onChange={e => setSmtp({...smtp, smtp_from_name: e.target.value})}
                  placeholder="Foreningens navn" />
              </div>
              <div className="form-group">
                <label>Afsender-email</label>
                <input className="form-control" value={smtp.smtp_from_email || ''}
                  onChange={e => setSmtp({...smtp, smtp_from_email: e.target.value})}
                  placeholder="forening@email.dk" />
              </div>
            </div>
            <div style={{display: 'flex', gap: '0.5rem'}}>
              <button type="submit" className="btn btn-primary">Gem SMTP</button>
              <button type="button" className="btn btn-secondary" disabled={testingSmtp}
                onClick={async () => {
                  setSmtpMessage(''); setSmtpError(''); setTestingSmtp(true);
                  try {
                    const r = await apiFetch('/api/settings/smtp/test', { method: 'POST' });
                    setSmtpMessage(r.message);
                  } catch (err) { setSmtpError(err.message); }
                  setTestingSmtp(false);
                }}>
                {testingSmtp ? 'Tester...' : 'Test forbindelse'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card" style={{marginTop: '1rem'}}>
        <h3 style={{marginBottom: '1rem'}}>Skift adgangskode</h3>
        <form onSubmit={changePassword}>
          <div className="form-group">
            <label>Nuværende adgangskode</label>
            <input type="password" className="form-control" value={pwForm.currentPassword}
              onChange={e => setPwForm({...pwForm, currentPassword: e.target.value})} required />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Ny adgangskode</label>
              <input type="password" className="form-control" value={pwForm.newPassword}
                onChange={e => setPwForm({...pwForm, newPassword: e.target.value})} required />
            </div>
            <div className="form-group">
              <label>Gentag ny adgangskode</label>
              <input type="password" className="form-control" value={pwForm.confirmPassword}
                onChange={e => setPwForm({...pwForm, confirmPassword: e.target.value})} required />
            </div>
          </div>
          <button type="submit" className="btn btn-primary">Skift adgangskode</button>
        </form>
      </div>

      {user?.role === 'admin' && (
        <div className="card" style={{marginTop: '2rem', borderLeft: '4px solid var(--danger)'}}>
          <h3 style={{marginBottom: '0.5rem', color: 'var(--danger)'}}>Farezone</h3>
          <p style={{fontSize: '0.875rem', color: 'var(--gray-700)', marginBottom: '1rem'}}>
            Slet alle bilag, fakturaer og bogføringslinjer fra systemet. Kontoplanen, medlemmer og indstillinger bevares.
          </p>
          {!showDeleteConfirm ? (
            <button className="btn btn-danger" onClick={() => setShowDeleteConfirm(true)}>
              Slet alle regnskabsposter
            </button>
          ) : (
            <div style={{background: '#fff5f5', border: '1px solid #feb2b2', borderRadius: '8px', padding: '1.25rem'}}>
              <p style={{fontWeight: 600, color: '#9b2c2c', marginBottom: '0.5rem'}}>
                Er du helt sikker?
              </p>
              <p style={{fontSize: '0.85rem', color: '#9b2c2c', marginBottom: '1rem'}}>
                Denne handling kan ikke fortrydes. Alle bilag, fakturaer og tilhørende bogføringslinjer bliver permanent slettet.
                Skriv <strong>SLET ALT</strong> herunder for at bekræfte.
              </p>
              <div className="form-group" style={{marginBottom: '1rem'}}>
                <input
                  className="form-control"
                  placeholder="Skriv SLET ALT"
                  value={deleteConfirmText}
                  onChange={e => setDeleteConfirmText(e.target.value)}
                  autoFocus
                />
              </div>
              <div style={{display: 'flex', gap: '0.5rem'}}>
                <button
                  className="btn btn-danger"
                  disabled={deleteConfirmText !== 'SLET ALT' || deleting}
                  onClick={deleteAccountingData}
                >
                  {deleting ? 'Sletter...' : 'Bekr\u00e6ft sletning'}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); }}
                >
                  Annuller
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
